import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AgenticGenieLogo from './assets/AgenticGenie.png';
import { useSystemApi } from './apis/system-apis';

const LoginForm = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, setSystemInfo, checkAuthStatus } = useAuth();
  const { getSystemInfo } = useSystemApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginError, setLoginError] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');

  // Mock user for local testing
  const mockUser = {
    email: 'test@example.com',
    name: 'Test User',
    // Add other required properties
  };

  // Use useCallback to memoize the fetchSystemInfo function
  const fetchSystemInfo = useCallback(async () => {
    try {
      console.log('Fetching system info in LoginForm');
      const systemInfo = await getSystemInfo();
      console.log('System info:', systemInfo);
      
      // Use the enhanced setSystemInfo function that persists to localStorage
      setSystemInfo(systemInfo);
      
      // As a backup, explicitly store in localStorage
      if (systemInfo) {
        localStorage.setItem('systemInfo', JSON.stringify(systemInfo));
      }
    } catch (error) {
      console.error('Error fetching system info:', error);
      
      // Try to use cached system info if available
      const cachedSystemInfo = localStorage.getItem('systemInfo');
      if (cachedSystemInfo) {
        try {
          const parsedInfo = JSON.parse(cachedSystemInfo);
          setSystemInfo(parsedInfo);
          console.log('Using cached system info:', parsedInfo);
        } catch (parseError) {
          console.error('Error parsing cached system info:', parseError);
        }
      }
    }
  }, [getSystemInfo, setSystemInfo]);

  useEffect(() => {
    // Fetch system info only once when component mounts
    fetchSystemInfo();
    
    // This part of the effect handles navigation based on auth state
    if (!isLoading && isAuthenticated) {
      navigate('/marketplace/skills', { replace: true });
    }
    
    // Skip authentication in local testing
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      console.log('skipping auth in LoginForm');
      navigate('/marketplace/skills', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]); // Remove fetchSystemInfo from dependencies

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    try {
      if (!username || !password) {
        setLoginError('Username and password are required.');
        setIsSubmitting(false);
        return;
      }
      // Use AuthService to sign in
      const { authService } = await import('./services/authService');
      const result = await authService.signIn({ username, password });
      
      if (result.nextStep && result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setLoginError('A new password is required. Please reset your password.');
        setIsSubmitting(false);
        return;
      }
      
      if (result.isSignedIn) {
        console.log('Login successful, updating authentication state');
        
        // Explicitly update the authentication state
        const isAuthUpdated = await checkAuthStatus();
        console.log('Authentication state updated:', isAuthUpdated);
        
        if (isAuthUpdated) {
          // Force a refresh of the system info
          await fetchSystemInfo();
          
          // Manually navigate to the skills page
          console.log('Navigating to skills page');
          navigate('/marketplace/skills', { replace: true });
        } else {
          setLoginError('Failed to update authentication state. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <div className="flex justify-center mb-6">
            <img src={AgenticGenieLogo} alt="Agentic Genie" className="h-40 w-auto" />
          </div>
          {/* <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your Marketplace account
          </h2> */}
        </div>
        
        <div className="mt-8 space-y-6">
          {loginError && (
            <div className="bg-red-50 text-red-500 p-4 rounded-md text-sm">
              {loginError}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex items-center justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginForm;